<script setup lang="ts">
import { buildUrlPrefix, getCategoryRoute, getTranslatedProperty, urlIsAbsolute } from '@shopware/helpers'
import type { Schemas } from '#shopware'

interface CategoryItem {
    afterId: string
    category: {
        translated: { name: string }
        active: boolean
        id: string
        seoUrl: string
    }
    children: Record<string, CategoryItem>
}

const props = defineProps<{
    categoryTree?: Record<string, CategoryItem> | null
    manufacturerId?: string | null
}>()

const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)
const { getUrlPrefix } = useUrlResolver()

const { category: activeCategory } = useCategory()

const navigationItems = inject<Ref<Schemas['NavigationRouteResponse'] | null>>('mainNavigation', ref(null))

// Damen / Herren / Kinder etc — always top-level navigation items
const mainCategories = computed<Schemas['Category'][]>(() =>
    (navigationItems.value as Schemas['Category'][] | null) ?? []
)

const activeMainCategory = computed(() =>
    mainCategories.value.find(mc =>
        mc.id === activeCategory.value?.id ||
        (activeCategory.value?.path ?? '').includes(mc.id)
    )
)

// Top Marken
const selectedManufacturers = useSelectedManufacturers(activeMainCategory)

const topBrandsExpanded = ref(false)

// Special page detection (/new/*, /sale/*)
const route = useRoute()
const { locale, defaultLocale } = useI18n()

const pathSegments = computed(() => {
    let path = route.path
    if (locale.value !== defaultLocale) {
        path = path.replace(new RegExp(`^/${locale.value}`), '') || '/'
    }
    return path.split('/').filter(Boolean)
})

const specialPrefix = computed(() => {
    const first = pathSegments.value[0]
    if (first === 'new') return 'new'
    if (first === 'sale') return 'sale'
    return null
})

const specialSubSlug = computed(() =>
    specialPrefix.value ? (pathSegments.value[1] ?? null) : null
)

// Find the "mirror" top-level nav category matching the sub-slug (e.g. "damenschuhe" from /new/damenschuhe)
const mirrorCategory = computed(() => {
    if (!specialSubSlug.value) return null
    const slug = specialSubSlug.value.toLowerCase()
    return mainCategories.value.find(mc =>
        mc.name?.toLowerCase() === slug ||
        (mc.seoUrls as Array<{ seoPathInfo: string }> | undefined)
            ?.[0]?.seoPathInfo?.replace(/\/$/, '').toLowerCase() === slug
    ) ?? null
})

const filterSourceCategory = computed(() => mirrorCategory.value ?? activeMainCategory.value)

const hasFilterItems = computed(() =>
    (filterSourceCategory.value?.children as Schemas['Category'][])?.length > 0
)

const expandedCategories = ref<Set<string>>(new Set())

function toggleCategory(id: string) {
    const next = new Set(expandedCategories.value)
    if (next.has(id)) {
        next.delete(id)
    } else {
        next.add(id)
    }
    expandedCategories.value = next
}

// Auto-expand the category that IS active or that contains the active category
watch(filterSourceCategory, (mc) => {
    if (!mc) return
    const next = new Set(expandedCategories.value)
    ;(mc.children as Schemas['Category'][] ?? []).forEach(cat => {
        if (cat.id === activeCategory.value?.id) {
            next.add(cat.id)
        }
        const containsActive = (cat.children as Schemas['Category'][])?.some(
            child => child.id === activeCategory.value?.id
        )
        if (containsActive) {
            next.add(cat.id)
        }
    })
    expandedCategories.value = next
}, { immediate: true })

function getCategoryUrl(category: Schemas['Category']): string {
    return buildUrlPrefix(getCategoryRoute(category), getUrlPrefix()).path
}

function isActive(category: Schemas['Category']): boolean {
    return category.id === activeCategory.value?.id
}

// Manufacturer tree mode — exclude root items that are ancestors of the active
// category (i.e. the Marken/manufacturer navigation branch)
const filteredCategoryTree = computed(() => {
    if (!props.categoryTree) return null
    const activePath = activeCategory.value?.path ?? ''
    return Object.fromEntries(
        Object.entries(props.categoryTree).filter(
            ([, item]) => item?.category && !activePath.includes(item.category.id)
        )
    )
})

const hasCategoryTree = computed(() =>
    !!filteredCategoryTree.value && Object.keys(filteredCategoryTree.value).length > 0
)

const expandedManufacturerCategories = ref<Set<string>>(new Set())
const expandedManufacturerSubCategories = ref<Set<string>>(new Set())

const sortedCategoryTree = computed(() => {
    if (!filteredCategoryTree.value) return []
    return Object.values(filteredCategoryTree.value)
        .filter(item => item?.category)
        .sort((a, b) => a.category.translated.name.localeCompare(b.category.translated.name))
        .map(item => ({
            ...item,
            children: Object.fromEntries(
                Object.entries(item.children ?? {})
                    .sort(([, a], [, b]) => a.category.translated.name.localeCompare(b.category.translated.name))
                    .map(([k, child]) => [k, {
                        ...child,
                        children: Object.fromEntries(
                            Object.entries(child.children ?? {})
                                .sort(([, a], [, b]) => a.category.translated.name.localeCompare(b.category.translated.name))
                        )
                    }])
            )
        }))
})

watch(filteredCategoryTree, (tree) => {
    if (!tree) return
    const next = new Set(expandedManufacturerCategories.value)
    Object.values(tree).forEach(item => {
        if (item?.category?.id) next.add(item.category.id)
    })
    expandedManufacturerCategories.value = next
}, { immediate: true })

function toggleExpanded(id: string) {
    const next = new Set(expandedManufacturerCategories.value)
    if (next.has(id)) {
        next.delete(id)
    } else {
        next.add(id)
    }
    expandedManufacturerCategories.value = next
}

function toggleSubExpanded(id: string) {
    const next = new Set(expandedManufacturerSubCategories.value)
    if (next.has(id)) {
        next.delete(id)
    } else {
        next.add(id)
    }
    expandedManufacturerSubCategories.value = next
}

function manufacturerCategoryUrl(seoUrl: string): string {
    const base = formatLink(seoUrl) as string
    return props.manufacturerId ? `${base}?manufacturer=${props.manufacturerId}` : base
}


const isSpecialRootPage = computed(() =>
    specialPrefix.value !== null && pathSegments.value.length === 1
)

// For root /new and /sale pages, find the real nav category matching a stub category by name/seoUrl
function getRealChildren(cat: Schemas['Category']): Schemas['Category'][] {
    if (!isSpecialRootPage.value) return (cat.children as Schemas['Category'][]) ?? []
    const name = getTranslatedProperty(cat, 'name')?.toLowerCase() ?? ''
    const real = mainCategories.value.find(mc =>
        mc.name?.toLowerCase() === name ||
        (mc.seoUrls as Array<{ seoPathInfo: string }> | undefined)
            ?.[0]?.seoPathInfo?.replace(/\/$/, '').toLowerCase() === name
    )
    return (real?.children as Schemas['Category'][]) ?? []
}

const specialParam = computed(() => {
    if (specialPrefix.value === 'new') return 'isNew=1'
    if (specialPrefix.value === 'sale') return 'isOnSale=1'
    return null
})

function appendFilterParam(url: string): string {
    if (!specialParam.value) return url
    if (url.includes('?')) return url
    const stripped = url.replace(/^\/(new|sale)\//, '/')
    return `${stripped}?${specialParam.value}`
}
</script>

<template>
    <!-- Manufacturer pages: navigation-link tree from categoryTree extension -->
    <ComponentDropdown
        v-if="hasCategoryTree"
        :trigger-label="$t('listing.filter.categoriesLabel')"
        class="w-full bg-base-200 border-0 border-b border-border btn-small justify-between font-bold px-4"
        content-classes="bg-base-100 w-full z-50"
    >
        <div class="flex flex-col">
            <template v-for="item in sortedCategoryTree" :key="item.category.id">
                <div class="flex items-center justify-between px-6 border-border border-b">
                    <FoundationLink
                        v-if="!urlIsAbsolute(item.category.seoUrl)"
                        :href="appendFilterParam(manufacturerCategoryUrl(item.category.seoUrl))"
                        :class="activeCategory?.id === item.category.id ? 'font-bold' : 'font-normal'"
                        class="py-2 block flex-1"
                    >
                        {{ item.category.translated.name }}
                    </FoundationLink>
                    <FoundationButton
                        v-if="Object.keys(item.children ?? {}).length"
                        variant="ghost"
                        square
                        size="small"
                        @click="toggleExpanded(item.category.id)"
                    >
                        <FoundationIcon :name="expandedManufacturerCategories.has(item.category.id) ? 'chevron-up' : 'chevron-down'" />
                    </FoundationButton>
                </div>
                <template v-if="expandedManufacturerCategories.has(item.category.id)">
                    <template v-for="(child, childId) in item.children" :key="childId">
                        <div v-if="child?.category" class="flex items-center justify-between pl-8 pr-6 border-border border-b">
                            <FoundationLink
                                v-if="!urlIsAbsolute(child.category.seoUrl)"
                                :href="appendFilterParam(manufacturerCategoryUrl(child.category.seoUrl))"
                                :class="activeCategory?.id === child.category.id ? 'font-bold' : 'font-normal'"
                                class="py-2 block flex-1"
                            >
                                {{ child.category.translated.name }}
                            </FoundationLink>
                            <FoundationButton
                                v-if="Object.keys(child.children ?? {}).length"
                                variant="ghost"
                                square
                                size="small"
                                @click="toggleSubExpanded(child.category.id)"
                            >
                                <FoundationIcon :name="expandedManufacturerSubCategories.has(child.category.id) ? 'chevron-up' : 'chevron-down'" />
                            </FoundationButton>
                        </div>
                        <template v-if="child?.category && expandedManufacturerSubCategories.has(child.category.id)">
                            <template v-for="(grandchild, grandchildId) in child.children" :key="grandchildId">
                                <FoundationLink
                                    v-if="grandchild?.category && !urlIsAbsolute(grandchild.category.seoUrl)"
                                    :href="appendFilterParam(manufacturerCategoryUrl(grandchild.category.seoUrl))"
                                    :class="activeCategory?.id === grandchild.category.id ? 'font-bold' : 'font-normal'"
                                    class="py-2 pl-14 block w-full border-border border-b"
                                >
                                    {{ grandchild.category.translated.name }}
                                </FoundationLink>
                            </template>
                        </template>
                    </template>
                </template>
            </template>
            <template v-if="selectedManufacturers.length">
                <div class="flex items-center justify-between px-6 border-border border-b">
                    <span class="py-2 flex-1">{{ $t('listing.filter.topBrandsLabel') }}</span>
                    <FoundationButton
                        variant="ghost"
                        square
                        size="small"
                        @click="topBrandsExpanded = !topBrandsExpanded"
                    >
                        <FoundationIcon :name="topBrandsExpanded ? 'chevron-up' : 'chevron-down'" />
                    </FoundationButton>
                </div>
                <template v-if="topBrandsExpanded">
                    <div
                        v-for="manufacturer in selectedManufacturers"
                        :key="manufacturer.id"
                        class="flex items-center justify-between px-6 border-border border-b"
                    >
                        <FoundationLink
                            :href="`${manufacturer.url}/${activeMainCategory?.name?.toLowerCase()}`"
                            class="py-2 block flex-1 font-normal"
                        >
                            {{ manufacturer.name }}
                        </FoundationLink>
                    </div>
                </template>
            </template>
        </div>
    </ComponentDropdown>

    <!-- Regular category pages -->
    <ComponentDropdown
        v-else-if="hasFilterItems"
        :trigger-label="$t('listing.filter.categoriesLabel')"
        class="w-full bg-base-200 border-0 border-b border-border btn-small justify-between font-bold px-4"
        content-classes="bg-base-100 w-full z-50"
    >
        <div class="flex flex-col">
            <template v-if="filterSourceCategory">
                <template v-for="cat in (filterSourceCategory.children as Schemas['Category'][])" :key="cat.id">
                    <div class="flex items-center justify-between px-6 border-border border-b">
                        <FoundationLink
                            v-if="!urlIsAbsolute(getCategoryUrl(cat))"
                            :href="appendFilterParam(formatLink(getCategoryUrl(cat)) as string)"
                            :class="isActive(cat) ? 'font-bold' : 'font-normal'"
                            class="py-2 block flex-1"
                        >
                            {{ getTranslatedProperty(cat, 'name') }}
                        </FoundationLink>
                        <FoundationButton
                            v-if="getRealChildren(cat).length"
                            variant="ghost"
                            square
                            size="small"
                            @click="toggleCategory(cat.id)"
                        >
                            <FoundationIcon :name="expandedCategories.has(cat.id) ? 'chevron-up' : 'chevron-down'" />
                        </FoundationButton>
                    </div>
                    <template v-if="expandedCategories.has(cat.id)">
                        <template v-for="child in getRealChildren(cat)" :key="child.id">
                            <FoundationLink
                                v-if="!urlIsAbsolute(getCategoryUrl(child))"
                                :href="appendFilterParam(formatLink(getCategoryUrl(child)) as string)"
                                :class="isActive(child) ? 'font-bold' : 'font-normal'"
                                class="py-2 pl-8 block w-full border-border border-b"
                            >
                                {{ getTranslatedProperty(child, 'name') }}
                            </FoundationLink>
                        </template>
                    </template>
                </template>
            </template>
            <template v-if="selectedManufacturers.length">
                <div class="flex items-center justify-between px-6 border-border border-b">
                    <span class="py-2 flex-1">{{ $t('listing.filter.topBrandsLabel') }}</span>
                    <FoundationButton
                        variant="ghost"
                        square
                        size="small"
                        @click="topBrandsExpanded = !topBrandsExpanded"
                    >
                        <FoundationIcon :name="topBrandsExpanded ? 'chevron-up' : 'chevron-down'" />
                    </FoundationButton>
                </div>
                <template v-if="topBrandsExpanded">
                    <div
                        v-for="manufacturer in selectedManufacturers"
                        :key="manufacturer.id"
                        class="flex items-center justify-between px-6 border-border border-b"
                    >
                        <FoundationLink
                            :href="`${manufacturer.url}/${activeMainCategory?.name?.toLowerCase()}`"
                            class="py-2 block flex-1 font-normal"
                        >
                            {{ manufacturer.name }}
                        </FoundationLink>
                    </div>
                </template>
            </template>
        </div>
    </ComponentDropdown>
</template>